import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Card, Form, Modal } from "react-bootstrap";
import { Toaster, toast } from "react-hot-toast";

import PurchaseLedgerPDF from "../components/PurchaseLedgerPDF";
import Pagination from "../components/Pagination";
import { PDFViewer } from "@react-pdf/renderer";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { parseApiPagination } from "../utils/parseApiPagination";
import {
  postUrdLedger,
  isErpApiSuccess,
  getErpRequestErrorMessage,
} from "../erpClient";

function toDateOnlyTimestamp(value) {
  if (!value) return null;
  const str = String(value).trim().split(" ")[0];
  let year;
  let month;
  let day;

  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    [year, month, day] = str.split("-").map(Number);
  } else if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
    const parts = str.split("/");
    day = Number(parts[0]);
    month = Number(parts[1]);
    year = Number(parts[2]);
  } else if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
    const parts = str.split("-");
    day = Number(parts[0]);
    month = Number(parts[1]);
    year = Number(parts[2]);
  } else {
    const parsed = new Date(str);
    if (Number.isNaN(parsed.getTime())) return null;
    year = parsed.getFullYear();
    month = parsed.getMonth() + 1;
    day = parsed.getDate();
  }

  return new Date(year, month - 1, day).getTime();
}

function UrdLedger() {
  const { buttonClass, cardClass } = useButtonTableStyle();
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [entireReport, setEntireReport] = useState(false);
  const [ledgerRows, setLedgerRows] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [pdfData, setPdfData] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMorePages, setHasMorePages] = useState(false);
  const ignoreNextPageFetchRef = useRef(false);
  const pageCacheRef = useRef(new Map());

  const fetchUrdLedger = useCallback(
    async ({
      requireDateRange = false,
      page = 1,
      limit = 10,
      silent = false,
      bypassCache = false,
    } = {}) => {
    const cacheKey = entireReport ? `${page}:${limit}` : "";
    if (cacheKey && !bypassCache) {
      const cached = pageCacheRef.current.get(cacheKey);
      if (cached) {
        setLedgerRows(cached.ledgerRows);
        setSummaryData(cached.summaryData);
        setCurrentPage(cached.currentPage);
        setTotalCount(cached.totalCount);
        setHasMorePages(cached.hasMorePages);
        setPdfData(cached.pdfData);
        setLoading(false);
        return;
      }
    }

    if (!entireReport) {
      if (requireDateRange && (!fromDate || !toDate)) {
        toast.error("Please select both From Date and To Date.");
        return;
      }
      if (fromDate && toDate) {
        const fromMs = toDateOnlyTimestamp(fromDate);
        const toMs = toDateOnlyTimestamp(toDate);
        if (fromMs != null && toMs != null && fromMs > toMs) {
          toast.error("From Date cannot be after To Date.");
          return;
        }
      }
    }

    setLoading(true);

    const apiPayload = {};

    if (entireReport) {
      apiPayload.type = "entire";
      apiPayload.page = page;
      apiPayload.limit = limit;
    } else {
      if (fromDate) apiPayload.from_date = fromDate;
      if (toDate) apiPayload.to_date = toDate;
    }

    try {
      const payload = await postUrdLedger(apiPayload);

      if (!payload || (!isErpApiSuccess(payload) && payload.message !== "radha")) {
        toast.error(
          payload?.message || "Failed to fetch ledger data. Please try again."
        );
        setLedgerRows([]);
        setSummaryData(null);
        return;
      }

      const entries = payload.data?.entries || payload.data || [];

      if (!Array.isArray(entries) || entries.length === 0) {
        toast.error("No ledger entries found for the selected date range.");
        setLedgerRows([]);
        setSummaryData(null);
        setTotalCount(0);
        return;
      }

      // Process entries
      let processedEntries = entries.map((entry) => {
        const rawDate =
          entry.date || entry.purchase_date || entry.bill_date || "";
        const dateMs = toDateOnlyTimestamp(rawDate);

        // Format date from YYYY-MM-DD to DD/MM/YYYY
        let formattedDate = rawDate || "-";
        if (formattedDate !== "-" && formattedDate.includes("-")) {
          const dateParts = formattedDate.split(" ")[0].split("-");
          if (dateParts.length === 3 && dateParts[0].length === 4) {
            formattedDate = `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`;
          }
        }

        // Format name: company_name (name) if name exists, otherwise just company_name
        let displayName = entry.company_name || entry.name || "-";
        if (entry.name && entry.name.trim() && entry.company_name) {
          displayName = `${entry.company_name} (${entry.name})`;
        } else if (entry.company_name) {
          displayName = entry.company_name;
        } else if (entry.name) {
          displayName = entry.name;
        }

        return {
          name: displayName,
          phone: entry.contact_number || entry.phone || entry.mobile || "-",
          purchase_no: entry.purchase_no || entry.voucher_number || "-",
          purchase_date: formattedDate,
          dateMs,
          purchase_value: parseFloat(
            entry.total_amount || entry.grand_total || entry.total_amt || 0
          ),
          taxable_value: parseFloat(
            entry.gross_amount ||
            entry.final_amount ||
            entry.taxable_value ||
            entry.total_amt ||
            0
          ),
          sgst: parseFloat(entry.sgst || 0),
          cgst: parseFloat(entry.cgst || 0),
          total_tax: parseFloat(
            entry.gst_amount || entry.total_tax || entry.gst || 0
          ),
          gold_gm: parseFloat(
            entry.gold_gm || entry.gold_weight || entry.total_fine || 0
          ),
          silver_gm: parseFloat(entry.silver_gm || entry.silver_weight || 0),
        };
      });

      if (!entireReport && fromDate && toDate) {
        const fromMs = toDateOnlyTimestamp(fromDate);
        const toMs = toDateOnlyTimestamp(toDate);
        processedEntries = processedEntries.filter((entry) => {
          if (entry.dateMs == null) return false;
          if (fromMs != null && entry.dateMs < fromMs) return false;
          if (toMs != null && entry.dateMs > toMs) return false;
          return true;
        });
      }

      processedEntries = processedEntries.map(({ dateMs, ...entry }) => entry);

      if (processedEntries.length === 0) {
        toast.error("No ledger entries found for the selected date range.");
        setLedgerRows([]);
        setSummaryData(null);
        return;
      }

      // Calculate summary totals
      const summary = processedEntries.reduce(
        (acc, entry) => {
          acc.purchase_value += entry.purchase_value;
          acc.taxable_value += entry.taxable_value;
          acc.sgst += entry.sgst;
          acc.cgst += entry.cgst;
          acc.total_tax += entry.total_tax;
          acc.gold_gm += entry.gold_gm;
          acc.silver_gm += entry.silver_gm;
          return acc;
        },
        {
          purchase_value: 0,
          taxable_value: 0,
          sgst: 0,
          cgst: 0,
          total_tax: 0,
          gold_gm: 0,
          silver_gm: 0,
        }
      );

      setLedgerRows(processedEntries);
      setSummaryData(summary);

      const paginationMeta = entireReport
        ? parseApiPagination(payload, processedEntries.length)
        : null;

      if (entireReport && paginationMeta) {
        setCurrentPage(paginationMeta.page);
        setTotalCount(paginationMeta.total);
        setHasMorePages(paginationMeta.hasMore);
        if (paginationMeta.limit) setItemsPerPage(paginationMeta.limit);
      } else {
        setCurrentPage(1);
        setTotalCount(processedEntries.length);
        setHasMorePages(false);
      }

      const nextPdf = {
        ledgerData: processedEntries,
        summaryData: summary,
        fromDate,
        toDate,
        entireReport,
      };
      setPdfData(nextPdf);
      if (cacheKey) {
        pageCacheRef.current.set(cacheKey, {
          ledgerRows: processedEntries,
          summaryData: summary,
          currentPage: paginationMeta?.page || page,
          totalCount: paginationMeta?.total ?? processedEntries.length,
          hasMorePages: Boolean(paginationMeta?.hasMore),
          pdfData: nextPdf,
        });
      }

      if (!silent) toast.success("Ledger generated successfully!");
    } catch (error) {
      toast.error(
        getErpRequestErrorMessage(
          error,
          "An error occurred while fetching the ledger data."
        )
      );
      console.error("Error fetching URD ledger:", error);
      setLedgerRows([]);
      setSummaryData(null);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
    },
    [entireReport, fromDate, toDate],
  );

  // Fetch data on component mount
  useEffect(() => {
    fetchUrdLedger({ page: 1, limit: itemsPerPage });
  }, [fetchUrdLedger]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for API URL changes and refetch data
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log('API URL changed in UrdLedger, refetching ledger...', event.detail);
      pageCacheRef.current.clear();
      fetchUrdLedger({
        page: currentPage,
        limit: itemsPerPage,
        silent: true,
        bypassCache: true,
      });
    };

    window.addEventListener('apiUrlChanged', handleApiUrlChange);

    return () => {
      window.removeEventListener('apiUrlChanged', handleApiUrlChange);
    };
  }, [fetchUrdLedger, currentPage, itemsPerPage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    pageCacheRef.current.clear();
    setCurrentPage(1);
    await fetchUrdLedger({
      requireDateRange: !entireReport,
      page: 1,
      limit: itemsPerPage,
      bypassCache: true,
    });
  };

  const handlePageChange = (page) => {
    if (ignoreNextPageFetchRef.current) {
      ignoreNextPageFetchRef.current = false;
      return;
    }
    setCurrentPage(page);
    fetchUrdLedger({ requireDateRange: false, page, limit: itemsPerPage, silent: true });
  };

  const handleItemsPerPageChange = (limit) => {
    ignoreNextPageFetchRef.current = true;
    pageCacheRef.current.clear();
    setItemsPerPage(limit);
    setCurrentPage(1);
    fetchUrdLedger({
      requireDateRange: false,
      page: 1,
      limit,
      silent: true,
      bypassCache: true,
    });
  };

  const handlePrint = () => {
    if (!pdfData || !ledgerRows.length) {
      toast.error("Please generate the ledger first.");
      return;
    }
    setShowPDFModal(true);
  };

  // Memoize the PDF component to avoid unnecessary rerenders
  const pdfViewer = useMemo(() => {
    if (!pdfData) return null;
    return (
      <PDFViewer width="100%" height="100%">
        <PurchaseLedgerPDF
          ledgerData={pdfData.ledgerData}
          summaryData={pdfData.summaryData}
          fromDate={pdfData.fromDate}
          toDate={pdfData.toDate}
          entireReport={pdfData.entireReport}
        />
      </PDFViewer>
    );
  }, [pdfData]);

  // Format currency with ₹ symbol and commas
  const formatCurrency = (value) => {
    if (!value || value === 0) return "-";
    return `₹ ${Math.round(value).toLocaleString("en-IN")}`;
  };

  // Format currency with decimals
  const formatCurrencyDecimal = (value) => {
    if (!value || value === 0) return "-";
    return `₹ ${value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  };

  // Format weight with 3 decimal places
  const formatWeight = (value) => {
    if (!value || value === 0) return "-";
    return value.toFixed(3);
  };

  return (
    <div className="body-wrapper">
      <Toaster />
      <div className="container-fluid">
        <div className="card">
          <div className="border-bottom title-part-padding">
            <h4 className="card-title mb-0">URD Ledger</h4>
          </div>
          <div className="card-body">
            <form noValidate="" onSubmit={handleSubmit}>
              <div className="row align-items-end">
                {/* From Date */}
                <div className="mb-3 col-md-3">
                  <label className="form-label">From Date</label>
                  <input
                    type="date"
                    className={`form-control ${buttonClass}`}
                    value={fromDate}
                    onChange={(e) => {
                      setFromDate(e.target.value);
                      setEntireReport(false);
                    }}
                    disabled={entireReport}
                  />
                </div>
                {/* To Date */}
                <div className="mb-3 col-md-3">
                  <label className="form-label">To Date</label>
                  <input
                    type="date"
                    className={`form-control ${buttonClass}`}
                    value={toDate}
                    onChange={(e) => {
                      setToDate(e.target.value);
                      setEntireReport(false);
                    }}
                    disabled={entireReport}
                  />
                </div>
                {/* Entire Report Checkbox */}
                <div
                  className="mb-3 col-md-3 d-flex align-items-center"
                  style={{ marginTop: 32 }}
                >
                  <Form.Check
                    type="checkbox"
                    id="entireReportCheckbox"
                    label="Entire Report"
                    checked={entireReport}
                    onChange={(e) => {
                      setEntireReport(e.target.checked);
                      setCurrentPage(1);
                    }}
                  />
                </div>
                {/* Submit Button */}
                <div
                  className="mb-3 col-md-3 d-flex align-items-center"
                  style={{ marginTop: 32 }}
                >
                  <button
                    type="submit"
                    className={`btn btn-primary w-100 ${buttonClass}`}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Loading...
                      </>
                    ) : (
                      "Submit"
                    )}
                  </button>
                </div>
              </div>
            </form>
            {/* Ledger Table */}
            {(ledgerRows.length > 0 || (entireReport && totalCount > 0)) && (
              <div className="mt-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">
                    URD Purchase
                    {entireReport && totalCount > 0 ? (
                      <span className="text-muted fs-6 ms-2">
                        (Page {currentPage} of{" "}
                        {Math.max(1, Math.ceil(totalCount / itemsPerPage))})
                      </span>
                    ) : null}
                  </h5>
                  <button
                    type="button"
                    className={`btn btn-secondary ${buttonClass}`}
                    onClick={handlePrint}
                  >
                    Print
                  </button>
                </div>
                <div className={`ledger-report-wrap mb-4 ${cardClass}`}>
                  <div className="table-responsive">
                    <table className="table text-nowrap mb-0 align-middle ledger-report-table">
                      <thead>
                        <tr>
                          <th colSpan="2" className="text-center">
                          Customer Details
                        </th>
                        <th
                          colSpan="3"
                          className="text-center"
                        >
                          PURCHASE DETAILS
                        </th>
                        <th
                          rowSpan="2"
                          className="text-end text-nowrap"
                        >
                          TAXABLE VALUE
                        </th>
                        <th
                          colSpan="3"
                          className="text-center"
                        >
                          AMOUNT OF TAX
                        </th>
                        <th
                          colSpan="2"
                          className="text-center"
                        >
                          WEIGHT
                        </th>
                      </tr>
                        <tr>
                        <th
                          className="text-nowrap"
                        >
                          NAME
                        </th>
                        <th
                          className="text-nowrap"
                        >
                          Phone
                        </th>
                        <th
                          className="text-nowrap"
                        >
                          PURCHASE NO
                        </th>
                        <th
                          className="text-nowrap"
                        >
                          PURCHASE DATE
                        </th>
                        <th
                          className="text-end text-nowrap"
                        >
                          PURCHASE VALUE
                        </th>
                        <th
                          className="text-end text-nowrap"
                        >
                          SGST
                        </th>
                        <th
                          className="text-end text-nowrap"
                        >
                          CGST
                        </th>
                        <th
                          className="text-end text-nowrap"
                        >
                          TOTAL TAX
                        </th>
                        <th
                          className="text-end text-nowrap"
                        >
                          GOLD (GM)
                        </th>
                        <th
                          className="text-end text-nowrap"
                        >
                          SILVER (GM)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerRows.map((row, idx) => (
                        <tr key={idx}>
                          <td
                          >
                            {row.name}
                          </td>
                          <td
                          >
                            {row.phone}
                          </td>
                          <td
                          >
                            {row.purchase_no}
                          </td>
                          <td
                          >
                            {row.purchase_date}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatCurrency(row.purchase_value)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatCurrencyDecimal(row.taxable_value)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatCurrencyDecimal(row.sgst)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatCurrencyDecimal(row.cgst)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatCurrencyDecimal(row.total_tax)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatWeight(row.gold_gm)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatWeight(row.silver_gm)}
                          </td>
                        </tr>
                      ))}
                      {/* Summary Row */}
                      {summaryData && (
                        <tr className="ledger-report-summary">
                          <td
                            colSpan="4"
                          ></td>
                          <td
                            className="text-end"
                          >
                            {formatCurrency(summaryData.purchase_value)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatCurrencyDecimal(summaryData.taxable_value)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatCurrencyDecimal(summaryData.sgst)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatCurrencyDecimal(summaryData.cgst)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatCurrencyDecimal(summaryData.total_tax)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatWeight(summaryData.gold_gm)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatWeight(summaryData.silver_gm)}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  </div>
                </div>
              </div>
            )}

            {entireReport && totalCount > 0 && (
              <Pagination
                totalItems={totalCount}
                hasMore={hasMorePages}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                itemsPerPageOptions={[10, 20, 30, 50, 100]}
              />
            )}
          </div>
        </div>
      </div>
      {/* PDF Modal */}
      <Modal
        show={showPDFModal}
        onHide={() => setShowPDFModal(false)}
        size="xl"
        centered
        dialogClassName="modal-90w"
      >
        <Modal.Header closeButton>
          <Modal.Title>URD Ledger PDF Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: "80vh" }}>
          {showPDFModal && pdfViewer}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default UrdLedger;
